
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";

interface DocumentImportTabProps {
  clientId?: string;
  clientName?: string;
  onSuccess?: () => void;
  onClose: () => void;
}

export function DocumentImportTab({ 
  clientId, 
  clientName, 
  onSuccess, 
  onClose 
}: DocumentImportTabProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (allowedTypes.includes(selectedFile.type)) {
        setFile(selectedFile);
      } else {
        toast.error("Please upload a PDF, Word document, or text file");
      }
    }
  };

  const handleProcess = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setIsProcessing(true);
    try {
      // This would be implemented to extract text from the document
      // and then use AI to create a job package from it
      toast.info("Document processing will be implemented in the next phase");
    } catch (error) {
      toast.error("Failed to process document");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6 py-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Upload Job Document
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="document">Job Posting Document</Label>
            <Input
              id="document"
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              className="mt-1"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Supported formats: PDF, Word documents, Text files
            </p>
          </div>

          {file && (
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm font-medium">{file.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({Math.round(file.size / 1024)} KB)
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How it works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">1</div>
              <div>
                <p className="font-medium">Upload your document</p>
                <p className="text-muted-foreground">Upload an existing job posting or job description document</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">2</div>
              <div>
                <p className="font-medium">AI extracts information</p>
                <p className="text-muted-foreground">Our AI reads and understands the document content</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">3</div>
              <div>
                <p className="font-medium">Generate optimized package</p>
                <p className="text-muted-foreground">Creates an enhanced job posting with performance predictions</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end pt-4 border-t sticky bottom-0 bg-background">
        <Button 
          onClick={handleProcess} 
          disabled={!file || isProcessing}
        >
          <Sparkles className="h-4 w-4 mr-2" />
          {isProcessing ? "Processing..." : "Process Document"}
        </Button>
      </div>
    </div>
  );
}
